/**
 * Narrows a long FAQ page to the questions that match what you typed.
 *
 * Not a search — nothing is fetched and nothing is ranked. Every answer is
 * already on the page; this only hides the ones that do not match, which is
 * why the box stays hidden until this script runs. A filter that cannot filter
 * is worse than no filter at all.
 *
 * Question and answer text are both searched. Someone looking for "trial"
 * should find the returns question whether or not the word is in its title.
 */
class FaqFilter extends HTMLElement {
  connectedCallback() {
    this.box = this.querySelector('[data-faq-filter]');
    this.input = this.querySelector('[data-faq-input]');
    this.empty = this.querySelector('[data-faq-empty]');
    if (!this.box || !this.input) return;

    this.topics = [...this.querySelectorAll('[data-faq-topic]')];
    this.items = [...this.querySelectorAll('[data-faq-item]')].map((item) => ({
      item,
      // Read once: the text does not change, and lowercasing every answer on
      // every keystroke is work for nothing.
      text: item.textContent.toLowerCase(),
    }));
    if (this.items.length === 0) return;

    this.box.hidden = false;

    this.onInput = this.onInput.bind(this);
    this.input.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
  }

  onInput() {
    const query = this.input.value.trim().toLowerCase();
    let matches = 0;

    for (const { item, text } of this.items) {
      const hit = query === '' || text.includes(query);
      item.hidden = !hit;
      if (hit) matches += 1;

      // Opened while filtering, every match would be expanded at once; closed
      // on the way out, the page returns to how it started.
      if (query !== '') item.open = hit;
      else item.open = false;
    }

    // A topic heading with every question hidden beneath it is a label for
    // nothing, so the whole group goes.
    for (const topic of this.topics) {
      const visible = [...topic.querySelectorAll('[data-faq-item]')].some((item) => !item.hidden);
      topic.hidden = !visible;
    }

    if (this.empty) this.empty.hidden = matches > 0;
  }
}

customElements.define('faq-filter', FaqFilter);
